import React, { useEffect, useMemo, useState } from 'react';
import { Wrapper } from './counter-requests-in-order.styles';
import { Typography } from '@shared/ui/typography/typography';
import { InputCount } from '@shared/ui/input-count/input-count';
import { CounterRequestsInOrderProps } from './types';
import toast from 'react-hot-toast';
import { Toast } from '@shared/ui/toast/toast';
import { getQueryError } from '@shared/lib/utils/get-query-error/get-query-error';
import { debounce } from '@shared/lib/utils/debounce/debounce';
import { useOrderSharedQuery } from '@entities/order/libs/hooks/use-order-shared-query/use-order-shared-query';
import { useUpdateRequestQuantityMutation } from '../api/counter-requests-in-order-api';
import { CACHE_KEY_MANAGER_UPDATE_QUANTITY_REQUEST } from '../config/api-constants';
import { ProductTypeCode } from '@shared/model/product';

export const CounterRequestsInOrder: React.FC<CounterRequestsInOrderProps> = ({ request, order, onUpdateCount }) => {
    const [, getOrderResult] = useOrderSharedQuery(order.id);

    const [updateRequest, updateRequestResult] = useUpdateRequestQuantityMutation({
        fixedCacheKey: `${CACHE_KEY_MANAGER_UPDATE_QUANTITY_REQUEST}_${request.id}`,
    });

    // State for optimistic-update quantity
    const [requestQuantity, setRequestQuantity] = useState(request.quantity);

    const quantityRequestsFromOrder = useMemo(() => {
        return getOrderResult.data?.requests.find((requestItem) => requestItem.id === request.id)?.quantity || 1;
    }, [getOrderResult, request]);

    const debouncedUpdateRequest = useMemo(() => {
        return debounce(updateRequest, 500);
    }, []);

    const handleChangeCount = (newCount: number) => {
        if (newCount >= 1) {
            setRequestQuantity(newCount);
            debouncedUpdateRequest({
                requestId: request.id,
                quantity: newCount,
                orderId: order.id,
            });
        }
    };

    useEffect(() => {
        setRequestQuantity(quantityRequestsFromOrder);
    }, [quantityRequestsFromOrder]);

    useEffect(() => {
        if (updateRequestResult.isSuccess) {
            setRequestQuantity(updateRequestResult.data.quantity);
            onUpdateCount();
            updateRequestResult.reset();
        } else if (updateRequestResult.isError) {
            toast.custom((t) => {
                return (
                    <Toast
                        type='error'
                        title='Ошибка!'
                        description={`Не удалось изменить количество продуктов.\n${getQueryError(updateRequestResult)?.message}`}
                        onClose={() => {
                            toast.remove(t.id);
                        }}
                    />
                );
            });
            updateRequestResult.reset();
        }
    }, [updateRequestResult.isSuccess, updateRequestResult.isError]);

    return (
        <Wrapper>
            <Typography size='body1_long' color='neutral_50'>
                Количество
            </Typography>
            <div onClick={(e) => e.stopPropagation()}>
                <InputCount
                    disabled={updateRequestResult.isLoading}
                    withoutControls={getOrderResult.data?.product?.type.code === ProductTypeCode.Demo}
                    value={requestQuantity}
                    min={1}
                    onChange={handleChangeCount}
                />
            </div>
        </Wrapper>
    );
};
